// Partner Mission Wall — confirmed sponsors as MISSION ACTIVATED case files.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getPartners } from "@/lib/api";

const year = (iso) => { try { return new Date(iso).getFullYear(); } catch { return ""; } };

export const PartnerWall = ({ showCta = true }) => {
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getPartners().then(setPartners).catch(() => setError(true));
  }, []);

  if (error) {
    return <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50" data-testid="partner-wall-error">Transmission interrupted — the wall could not be loaded.</p>;
  }
  if (!partners) {
    return <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50" data-testid="partner-wall-loading">Decrypting partner files...</p>;
  }
  if (partners.length === 0) {
    return (
      <div className="gold-frame grain-dark relative mx-auto max-w-xl bg-charcoal/80 p-8 text-center sm:p-12" data-testid="partner-wall-empty">
        <div className="pointer-events-none absolute inset-2 border border-gold/15" />
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">
          <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
          Awaiting first activation
        </p>
        <h3 className="mt-4 font-display text-2xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-4xl">
          No missions activated yet.
        </h3>
        <p className="mx-auto mt-4 max-w-sm font-body text-sm leading-relaxed text-silver/80">
          Your name could be the first on this wall.
        </p>
        {showCta && (
          <Link to="/transmission/industry" data-testid="partner-wall-empty-cta"
            className="btn-sweep mt-7 inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-7 py-3 font-head text-xs font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
            Activate the First Mission <ArrowRight size={14} />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="partner-wall">
      {partners.map((p, i) => (
        <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
          <div className="group relative h-full border border-gold/30 bg-charcoal/80 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-[0_0_30px_rgba(201,162,39,0.14)]" data-testid={`partner-file-${p.id}`}>
            <div className="pointer-events-none absolute inset-1.5 border border-gold/10" />
            <div className="relative">
              <div className="flex items-start justify-between gap-3">
                <span className="inline-flex -rotate-2 items-center gap-1.5 border border-gold px-2.5 py-1 font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-gold shadow-[0_0_18px_rgba(201,162,39,0.15)]">
                  <BadgeCheck size={11} /> Mission Activated
                </span>
                {p.activated_at && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-silver/40">{year(p.activated_at)}</span>
                )}
              </div>
              <h3 className="mt-5 font-display text-2xl uppercase leading-[0.95] tracking-tight text-gold-metallic sm:text-3xl" data-testid="partner-name">{p.name}</h3>
              <p className="mt-2 font-cinzel text-[10px] uppercase tracking-[0.3em] text-cream/85">{p.contribution}</p>
              {p.mission_title && (
                <p className="mt-3 font-body text-sm leading-relaxed text-silver/75">
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-silver/45">Operation · </span>{p.mission_title}
                </p>
              )}
              {p.world && (
                <span className="mt-3 inline-block border border-silver/25 bg-onyx px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-silver/70">{p.world}</span>
              )}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
};
